import { ref } from 'vue'
import { useQuasar } from 'quasar'
import { CapacitorBrowser } from 'boot/inject-capacitor'

export interface DownloadImageOptions {
  imageUrl: string
  fileName: string
  shareTitle: string
  shareText: string
  successMessage: string
  errorMessage: string
  onDownloadStarted?: () => void
}

export function useShareDialogs() {
  const $q = useQuasar()
  const loadingImageDownload = ref(false)

  /**
   * Downloads an image file using CapacitorBrowser on mobile for native download experience
   * @param options - Download configuration options
   */
  async function downloadImageCapacitor(options: DownloadImageOptions) {
    try {
      // Add download parameter to trigger automatic download
      const downloadUrl = new URL(options.imageUrl)
      downloadUrl.searchParams.set('m', 'download')
      const finalUrl = downloadUrl.toString()

      console.log('Opening image in browser for download:', finalUrl)

      // Use CapacitorBrowser to open the image URL
      // This will trigger the native browser download functionality
      if (CapacitorBrowser && CapacitorBrowser.open) {
        await CapacitorBrowser.open({
          url: finalUrl,
          windowName: '_system', // Opens in system browser for better download handling
        })

        // Call download started callback
        options.onDownloadStarted?.()
      } else {
        throw new Error('Browser plugin not available')
      }
    } catch (error) {
      console.error('Error opening image in browser:', error)

      // Fallback: try to open URL directly in a new window/tab with download parameter
      try {
        const downloadUrl = new URL(options.imageUrl)
        downloadUrl.searchParams.set('m', 'download')
        window.open(downloadUrl.toString(), '_blank')
        options.onDownloadStarted?.()
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError)
        $q.notify({
          type: 'negative',
          message: options.errorMessage,
        })
      }
    } finally {
      loadingImageDownload.value = false
    }
  }

  /**
   * Downloads an image file using web browser download functionality
   * @param options - Download configuration options
   */
  function downloadImageWeb(options: DownloadImageOptions) {
    fetch(options.imageUrl)
      .then((response) => {
        console.log(
          'Fetch response status:',
          response.status,
          response.statusText
        )
        console.log(
          'Response content-type:',
          response.headers.get('content-type')
        )
        console.log(
          'Response content-length:',
          response.headers.get('content-length')
        )

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        return response.blob()
      })
      .then((blob) => {
        console.log('Image blob size:', blob.size, 'bytes, type:', blob.type)
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = options.fileName
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)

        // Call download started callback
        options.onDownloadStarted?.()
      })
      .catch((err) => {
        console.error('Error downloading image:', err)
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack,
        })
        $q.notify({
          type: 'negative',
          message: options.errorMessage,
        })
      })
      .finally(() => {
        loadingImageDownload.value = false
      })
  }

  /**
   * Downloads an image with appropriate method based on platform
   * @param options - Download configuration options
   */
  function downloadImage(options: DownloadImageOptions) {
    loadingImageDownload.value = true
    console.log('Attempting to download image from:', options.imageUrl)

    // Check if running on Capacitor (mobile) or web
    if ($q.platform.is.capacitor && CapacitorBrowser) {
      // Mobile: Use browser to open image for native download experience
      downloadImageCapacitor(options)
    } else {
      // Web: Use traditional blob download
      downloadImageWeb(options)
    }
  }

  /**
   * Saves an image to the device gallery or downloads it on web
   * @param imageUrl - URL of the image to save
   * @param fileName - Optional filename for the saved image
   * @returns Promise<boolean> - true if successful, false if failed
   */
  async function saveImageToGallery(
    imageUrl: string,
    fileName?: string
  ): Promise<boolean> {
    try {
      if ($q.platform.is.nativeMobile && CapacitorBrowser) {
        // On mobile, open image in browser for native save/share functionality
        // Add download parameter to trigger automatic download
        const downloadUrl = new URL(imageUrl)
        downloadUrl.searchParams.set('m', 'download')

        await CapacitorBrowser.open({
          url: downloadUrl.toString(),
          windowName: '_system',
        })

        return true
      } else {
        // On web, fall back to regular download
        downloadImage({
          imageUrl,
          fileName: fileName || `image-${Date.now()}.jpg`,
          shareTitle: 'Bild speichern',
          shareText: 'Bild öffnen mit...',
          successMessage: 'Bild heruntergeladen',
          errorMessage: 'Fehler beim Herunterladen',
        })
        return true
      }
    } catch (error) {
      console.error('Failed to save to gallery:', error)
      $q.notify({
        type: 'negative',
        message: 'Fehler beim Öffnen des Bildes',
        timeout: 3000,
      })
      return false
    }
  }

  return {
    loadingImageDownload,
    downloadImage,
    saveImageToGallery,
  }
}
